import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Plus, Tags } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth';
import { useExpenses, useExpenseFilters } from '../hooks';
import { ExpensesDataTable, ExpenseDialog } from '../components';
import type { Expense } from '../types';

/**
 * Company expenses page — a status-filtered list of finance records, optionally
 * linked to a matter (case) and/or a contract. Permitted users create/edit
 * expenses and move them through the NEW → PENDING → ACCEPTED / DENIED workflow
 * (row actions mirror the Payments page).
 */
export function ExpensesPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, resetFilters, setPage, setLimit } = useExpenseFilters();

  const { data, isLoading, isError } = useExpenses(filters);

  const canCreate = hasAccess('expense', 'create');

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Expense | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (expense: Expense) => {
    setEditing(expense);
    setDialogOpen(true);
  };

  // Slot in the title row the table portals its Columns/Filters buttons into.
  const [tableControlsEl, setTableControlsEl] = React.useState<HTMLDivElement | null>(null);

  const expenses = data?.results ?? [];

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('expense.pageTitle')}</h1>
        <div className="flex items-center gap-3">
          <div ref={setTableControlsEl} className="flex items-center gap-2" />
          {hasAccess('expense-category', 'get') && (
            <Link to="/app/settings/expense-categories">
              <Button variant="outline">
                <Tags className="mr-2 h-4 w-4" />
                {t('expenseCategory.pageTitle')}
              </Button>
            </Link>
          )}
          {canCreate && (
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              {t('expense.add')}
            </Button>
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <ExpensesDataTable
          controlsContainer={tableControlsEl}
          data={expenses}
          total={data?.meta?.total ?? 0}
          filters={filters}
          isLoading={isLoading}
          isError={isError}
          onFiltersChange={updateFilter}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onClearFilters={resetFilters}
          onEdit={openEdit}
          onCreate={canCreate ? openCreate : undefined}
          hasAccess={hasAccess}
        />
      </div>

      <ExpenseDialog open={dialogOpen} onOpenChange={setDialogOpen} expense={editing} />
    </div>
  );
}
